import { logger } from "./logger";
import { safeStorage } from './safariStorage';

/**
 * Security utility functions for the application
 */

/**
 * Sanitizes user input to prevent XSS and injection attacks
 */
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .trim()
    .replace(/[<>\"'&]/g, '') // Remove potentially dangerous characters
    .substring(0, 500); // Limit length to prevent buffer overflow
};

/**
 * Validates email format with security considerations
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(email) && email.length <= 254; // RFC 5321 limit
};

/**
 * Validates phone number format
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') return false;
  
  const phoneRegex = /^[+]?[\d\s\-\(\)]{10,20}$/;
  return phoneRegex.test(phone);
};

/**
 * Calculates password entropy based on character set and length
 * @param password - The password to analyze
 * @returns Entropy value in bits
 */
export const calculatePasswordEntropy = (password: string): number => {
  let charset = 0;
  
  if (/[a-z]/.test(password)) charset += 26; // lowercase
  if (/[A-Z]/.test(password)) charset += 26; // uppercase
  if (/\d/.test(password)) charset += 10; // numbers
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) charset += 32; // special chars
  
  return Math.log2(Math.pow(charset, password.length));
};

/**
 * Checks if password contains common dictionary words or patterns
 * @param password - The password to check
 * @returns Array of detected issues
 */
export const checkPasswordPatterns = (password: string): string[] => {
  const issues: string[] = [];
  const lowerPassword = password.toLowerCase();
  
  // Common dictionary words
  const commonWords = [
    'password', 'admin', 'user', 'login', 'welcome', 'hello', 'goodbye',
    'computer', 'internet', 'website', 'email', 'phone', 'address',
    'birthday', 'anniversary', 'family', 'friend', 'love', 'hate'
  ];
  
  // Check for dictionary words
  for (const word of commonWords) {
    if (lowerPassword.includes(word)) {
      issues.push(`Avoid using common words like "${word}"`);
      break; // Only show one dictionary word warning
    }
  }
  
  // Check for keyboard patterns
  const keyboardPatterns = [
    'qwerty', 'asdf', 'zxcv', '123456', 'abcdef',
    'qwertyuiop', 'asdfghjkl', 'zxcvbnm'
  ];
  
  for (const pattern of keyboardPatterns) {
    if (lowerPassword.includes(pattern)) {
      issues.push('Avoid keyboard patterns');
      break;
    }
  }
  
  // Check for sequences
  const hasSequence = /(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/.test(lowerPassword);
  if (hasSequence) {
    issues.push('Avoid sequential characters');
  }
  
  return issues;
};

/**
 * Checks if password contains personal information
 * @param password - The password to check
 * @param personalInfo - Object containing user's personal information
 * @returns Array of detected personal info usage
 */
export const checkPersonalInfo = (password: string, personalInfo: { 
  name?: string; 
  email?: string; 
  phone?: string; 
  company?: string; 
} = {}): string[] => {
  const issues: string[] = [];
  const lowerPassword = password.toLowerCase();
  
  if (personalInfo.name) {
    const nameParts = personalInfo.name.toLowerCase().split(' ');
    for (const part of nameParts) {
      if (part.length > 2 && lowerPassword.includes(part)) {
        issues.push('Avoid using your name');
        break;
      }
    }
  }
  
  if (personalInfo.email) {
    const emailPrefix = personalInfo.email.split('@')[0].toLowerCase();
    if (emailPrefix.length > 2 && lowerPassword.includes(emailPrefix)) {
      issues.push('Avoid using parts of your email');
    }
  }
  
  if (personalInfo.company && personalInfo.company.length > 2) {
    if (lowerPassword.includes(personalInfo.company.toLowerCase())) {
      issues.push('Avoid using your company name');
    }
  }
  
  return issues;
};

/**
 * Comprehensive password strength analysis
 * @param password - The password to analyze
 * @param personalInfo - Optional personal information to check against
 * @returns Detailed strength analysis
 */
export const analyzePasswordStrength = (
  password: string, 
  personalInfo: { name?: string; email?: string; phone?: string; company?: string; } = {}
): {
  score: number; // 0-100
  level: 'weak' | 'fair' | 'good' | 'strong' | 'excellent';
  entropy: number;
  errors: string[];
  suggestions: string[];
  isValid: boolean;
} => {
  const errors: string[] = [];
  const suggestions: string[] = [];
  let score = 0;
  
  // Basic requirements
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  } else {
    score += 20;
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  } else {
    score += 15;
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  } else {
    score += 15;
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  } else {
    score += 15;
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>\-_~\[\]\\\/;'`=+]/.test(password)) {
    errors.push('Password must contain at least one special character');
  } else {
    score += 15;
  }
  
  // Calculate entropy and adjust score
  const entropy = calculatePasswordEntropy(password);
  if (entropy >= 60) score += 10;
  else if (entropy >= 40) score += 5;
  
  // Check for patterns and personal info
  const patternIssues = checkPasswordPatterns(password);
  const personalIssues = checkPersonalInfo(password, personalInfo);
  
  // Deduct points for issues
  score -= (patternIssues.length * 10);
  score -= (personalIssues.length * 15);
  
  // Add issues to errors
  errors.push(...patternIssues);
  errors.push(...personalIssues);
  
  // Ensure score is between 0-100
  score = Math.max(0, Math.min(100, score));
  
  // Determine level
  let level: 'weak' | 'fair' | 'good' | 'strong' | 'excellent';
  if (score >= 90) level = 'excellent';
  else if (score >= 75) level = 'strong';
  else if (score >= 60) level = 'good';
  else if (score >= 40) level = 'fair';
  else level = 'weak';
  
  // Generate suggestions
  if (password.length < 12) suggestions.push('Consider using 12+ characters for better security');
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) suggestions.push('Add special characters for stronger security');
  if (entropy < 50) suggestions.push('Use a more diverse mix of characters');
  if (patternIssues.length > 0) suggestions.push('Avoid predictable patterns');
  if (personalIssues.length > 0) suggestions.push('Avoid personal information');
  
  return {
    score,
    level,
    entropy,
    errors,
    suggestions,
    isValid: errors.length === 0 && score >= 60
  };
};

/**
 * Validates password strength (client-side only, never log passwords)
 */
export const validatePasswordStrength = (password: string): { 
  isValid: boolean; 
  errors: string[] 
} => {
  const analysis = analyzePasswordStrength(password);
  return {
    isValid: analysis.isValid,
    errors: analysis.errors
  };
};

/**
 * Rate limiting helper - tracks authentication attempts
 */
class RateLimiter {
  private attempts: Map<string, { count: number; lastAttempt: number }> = new Map();
  private readonly maxAttempts = 5;
  private readonly windowMs = 15 * 60 * 1000; // 15 minutes

  isRateLimited(identifier: string): boolean {
    const now = Date.now();
    const record = this.attempts.get(identifier);
    
    if (!record) return false;
    
    // Reset if window has passed
    if (now - record.lastAttempt > this.windowMs) {
      this.attempts.delete(identifier);
      return false;
    }
    
    return record.count >= this.maxAttempts;
  }
  
  recordAttempt(identifier: string): void {
    const now = Date.now();
    const record = this.attempts.get(identifier);
    
    if (!record || now - record.lastAttempt > this.windowMs) {
      this.attempts.set(identifier, { count: 1, lastAttempt: now });
    } else {
      record.count++;
      record.lastAttempt = now;
    }
  }
  
  reset(identifier: string): void {
    this.attempts.delete(identifier);
  }
}

export const authRateLimiter = new RateLimiter();

/**
 * Rate limiter specifically for email change requests
 */
export const emailChangeRateLimiter = {
  key: 'email_change_attempts',
  maxAttempts: 3,
  windowMs: 3600000, // 1 hour
  
  isRateLimited(userId: string): boolean {
    const key = `${this.key}_${userId}`;
    const stored = safeStorage.getItem(key);
    
    if (!stored) return false;
    
    try {
      const attempts = JSON.parse(stored) as number[];
      const now = Date.now();
      
      // Filter out old attempts
      const recentAttempts = attempts.filter(
        (timestamp: number) => now - timestamp < this.windowMs
      );
      
      // Update storage with filtered attempts
      safeStorage.setItem(key, JSON.stringify(recentAttempts));
      
      return recentAttempts.length >= this.maxAttempts;
    } catch {
      return false;
    }
  },
  
  recordAttempt(userId: string): void {
    const key = `${this.key}_${userId}`;
    const stored = safeStorage.getItem(key);
    
    try {
      const attempts = stored ? JSON.parse(stored) as number[] : [];
      attempts.push(Date.now());
      safeStorage.setItem(key, JSON.stringify(attempts));
    } catch {
      // If storage fails, just continue
      safeStorage.setItem(key, JSON.stringify([Date.now()]));
    }
  },
  
  reset(userId: string): void {
    const key = `${this.key}_${userId}`;
    safeStorage.removeItem(key);
  },
  
  getRemainingTime(userId: string): number {
    const key = `${this.key}_${userId}`;
    const stored = safeStorage.getItem(key);
    
    if (!stored) return 0;
    
    try {
      const attempts = JSON.parse(stored) as number[];
      if (attempts.length === 0) return 0;
      
      const oldestAttempt = Math.min(...attempts);
      const elapsed = Date.now() - oldestAttempt;
      const remaining = this.windowMs - elapsed;
      
      return remaining > 0 ? Math.ceil(remaining / 60000) : 0; // Return minutes
    } catch {
      return 0;
    }
  }
};

/**
 * Secure error messages that don't expose sensitive information
 */
export const getSecureErrorMessage = (error: any): string => {
  // Log full error for debugging but return sanitized message to user
  logger.error('Authentication error occurred', { error: error?.message || 'Unknown error' });
  
  // Return generic messages that don't expose system details
  const msgLower = (error?.message || '').toLowerCase();
  if (
    error?.message?.includes('Invalid login credentials') ||
    error?.code === 'invalid_credentials' ||
    msgLower.includes('invalid login') ||
    msgLower.includes('invalid credentials') ||
    (error?.status === 400 && msgLower.includes('credentials'))
  ) {
    return 'Invalid email or password. Please try again.';
  }
  
  if (error?.message?.includes('Email not confirmed')) {
    return 'Please check your email and click the confirmation link before signing in.';
  }
  
  if (error?.message?.includes('Too many requests')) {
    return 'Too many attempts. Please wait a few minutes before trying again.';
  }
  
  // Handle email rate limiting (422 from Supabase)
  if (error?.message?.includes('over_email_send_rate_limit') || 
      error?.code === 'over_email_send_rate_limit' ||
      (error?.status === 422 && error?.message?.toLowerCase().includes('email'))) {
    return 'Email sending is temporarily limited for this address. Please wait a few minutes or try with a different email.';
  }
  
  // Handle 422 errors - only treat as email throttling if message indicates email issues
  if (error?.status === 422) {
    const msg = (error?.message || '').toLowerCase();
    if (msg.includes('email') || msg.includes('rate') || msg.includes('throttl')) {
      return 'Email sending is temporarily limited. Please wait a few minutes or try with a different email.';
    }
    // For other 422 errors, return the actual message or a generic validation error
    return error?.message || 'There was an issue with your request. Please check your information and try again.';
  }
  
  // Handle "user already exists" errors
  if (error?.message?.includes('already registered') || 
      error?.message?.includes('User already registered') ||
      error?.code === 'user_already_exists') {
    return 'An account with this email already exists. Please log in instead.';
  }
  
  // Handle password breach database rejection (HIBP check)
  if (error?.message?.includes('known to be weak') || 
      error?.message?.includes('easy to guess') ||
      error?.message?.includes('have I been pwned') ||
      error?.message?.includes('pwned') ||
      error?.message?.includes('data breach')) {
    return 'This password has been found in a known data breach and cannot be used for security reasons. Please choose a completely different password - try using the "Generate secure password" button for a unique, random password.';
  }
  
  // Generic fallback that doesn't expose system internals
  return 'Authentication failed. Please try again or contact support if the problem persists.';
};

/**
 * Sanitizes user profile data before saving
 */
export const sanitizeProfileData = (data: any): any => {
  if (!data || typeof data !== 'object') return {};
  
  const sanitized: any = {};
  
  // Sanitize string fields
  const stringFields = ['full_name', 'company_name', 'phone_number', 'address'];
  stringFields.forEach(field => {
    if (data[field]) {
      sanitized[field] = sanitizeInput(data[field]);
    }
  });
  
  // Validate email separately
  if (data.email && isValidEmail(data.email)) {
    sanitized.email = data.email.toLowerCase().trim();
  }
  
  // Validate numeric fields
  if (data.deals_annually && typeof data.deals_annually === 'number') {
    sanitized.deals_annually = Math.max(0, Math.min(10000, data.deals_annually));
  }
  
  // Sanitize array fields
  if (Array.isArray(data.preferred_markets)) {
    sanitized.preferred_markets = data.preferred_markets
      .filter(market => typeof market === 'string')
      .map(market => sanitizeInput(market))
      .slice(0, 50); // Limit array size
  }
  
  if (Array.isArray(data.purchasing_type)) {
    sanitized.purchasing_type = data.purchasing_type
      .filter(type => typeof type === 'string')
      .map(type => sanitizeInput(type))
      .slice(0, 10); // Limit array size
  }
  
  return sanitized;
};
